import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SidebarList from "../../components/layout/WithFixedSidebar/SidebarList/SidebarList";
import WithFixedSidebar from "../../components/layout/WithFixedSidebar/WithFixedSidebar";
import Dialog from "@mui/material/Dialog";
import portraitIcon from "../../assets/images/icons/portrait.svg";
import scss from "../../components/layout/WithFixedSidebar/SidebarList/SidebarList.module.scss";
import profileScss from "./ProfilePage.module.scss";
import useAPI from "hooks/useAPI";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import AuthContext from "store/auth-context";
import Sitemap from "routes/sitemap";

/**
 * Show the options regarding the profile.
 *
 * @return {JSX.Element}
 * @constructor
 */
const ProfilePage = ({ children }) => {
    const { t, i18n } = useTranslation();

    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { logout } = useContext(AuthContext);
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const api = useAPI();
    const { data: profilePageList } = useQuery(["ProfilePage"], () => api.get(`/profile/menu/left-menu`));
    const activeOption = profilePageList?.find((option) => option.route === pathname) ?? profilePageList?.find((option) => pathname.startsWith(`${option.route}/`));

    const openProfilePage = (route) => {
        setMobileMenuOpen(false);
        navigate(route);
    };

    const logOut = () => {
        const defaultLanguage = localStorage.getItem("defaultLanguage");
        localStorage.removeItem("selectedLanguage");
        if (defaultLanguage) {
            i18n.changeLanguage(defaultLanguage);
        }
        setShowLogoutDialog(false);
        logout();
        navigate(Sitemap.auth.login);
    };

    // Build the sidebar
    const sidebarList = (
        <SidebarList>
            {profilePageList &&
                profilePageList.map((option) => (
                    <div key={option.route} className={option.route === pathname ? scss.active : ""} onClick={() => navigate(option.route)}>
                        {option.label}
                    </div>
                ))}
            <button
                type="button"
                className={profileScss.logout}
                onClick={(event) => {
                    event.stopPropagation();
                    setShowLogoutDialog(true);
                }}
            >
                Odjavite se
            </button>
        </SidebarList>
    );

    return (
        <>
            <WithFixedSidebar header={t("global-default-text-my_account")} icon={portraitIcon} sidebar={sidebarList} mobileNavigation={false}>
                <nav className={profileScss.mobileNavigation} aria-label={t("global-default-text-my_account")}>
                    <button type="button" className={profileScss.mobileNavigationToggle} aria-expanded={mobileMenuOpen} onClick={() => setMobileMenuOpen((open) => !open)}>
                        <span>{activeOption?.label ?? t("global-default-text-my_account")}</span>
                        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                    {mobileMenuOpen && (
                        <div className={profileScss.mobileNavigationList}>
                            {profilePageList?.map((option) => (
                                <button type="button" key={option.route} className={option.route === activeOption?.route ? profileScss.mobileNavigationActive : ""} onClick={() => openProfilePage(option.route)}>
                                    {option.label}
                                </button>
                            ))}
                            <button type="button" className={profileScss.mobileLogout} onClick={() => { setMobileMenuOpen(false); setShowLogoutDialog(true); }}>
                                Odjavite se
                            </button>
                        </div>
                    )}
                </nav>
                {children}
            </WithFixedSidebar>
            <Dialog open={showLogoutDialog} onClose={() => setShowLogoutDialog(false)} aria-labelledby="logout-dialog-title" PaperProps={{ className: profileScss.dialogPaper }}>
                <div className={profileScss.dialogHeader}>
                    <span className={profileScss.dialogIcon} aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none">
                            <path d="M10 4.5H6.5A2.5 2.5 0 0 0 4 7v10a2.5 2.5 0 0 0 2.5 2.5H10M13 7l5 5-5 5M18 12H9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </span>
                    <h2 id="logout-dialog-title">{t("global-left_side_bar-delete_dialog-title")}</h2>
                    <button type="button" className={profileScss.dialogClose} onClick={() => setShowLogoutDialog(false)} aria-label={t("global-default-text-Cancel")}>
                        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="m5 5 10 10M15 5 5 15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
                <p className={profileScss.dialogDescription}>{t("global-left_side_bar-delete_dialog-description")}</p>
                <div className={profileScss.dialogActions}>
                    <button type="button" className={profileScss.cancelButton} onClick={() => setShowLogoutDialog(false)}>{t("global-default-text-Cancel")}</button>
                    <button type="button" className={profileScss.confirmButton} onClick={logOut}>{t("global-left_side_bar-delete_dialog-button")}</button>
                </div>
            </Dialog>
        </>
    );
};

export default ProfilePage;
